import React from 'react';
import styled, { keyframes } from 'styled-components';
import Color from 'color';
import LayersOutlined from '@mui/icons-material/LayersOutlined';
import QrCode2Outlined from '@mui/icons-material/QrCode2Outlined';
import {
  parseCombinedSkuDetailsPreview,
} from './auditsDisplayLogs';

const lFColor0 = '#7914cc';
const inkMuted = '#6c757d';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(3px); }
  to { opacity: 1; transform: translateY(0); }
`;

const CombinedLead = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 700;
  vertical-align: middle;
  white-space: nowrap;
  margin-inline: 2px;
  animation: ${fadeIn} 180ms ease both;
`;

const CombinedLeadIcon = styled.span`
  display: inline-flex;
  color: ${lFColor0};
  line-height: 0;
`;

const CombinedJoiner = styled.span`
  font-weight: 500;
  color: ${inkMuted};
  margin-inline: 2px;
`;

const CombinedTypeChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  vertical-align: middle;
  margin: 1px 2px;
  padding: 1px 7px 1px 5px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 11px;
  line-height: 1.35;
  color: ${Color(lFColor0).darken(0.12).toString()};
  animation: ${fadeIn} 180ms ease both;
`;

const CombinedTypeChipIcon = styled.span`
  display: inline-flex;
  color: ${lFColor0};
  line-height: 0;
  opacity: 0.85;
`;

const CombinedMoreChip = styled(CombinedTypeChip)`
  font-weight: 700;
  color: ${inkMuted};
  background: ${Color(inkMuted).alpha(0.1).toString()};
`;

/** Combined SKU details — log count + type chips. Falls back to plain text. */
export const CombinedSkuDetailsText = ({ text }: { text: string }) => {
  const body = String(text || '');
  const parts = parseCombinedSkuDetailsPreview(body);
  if (!parts) return <>{body}</>;

  const logLabel = `${parts.count.toLocaleString()} log${parts.count === 1 ? '' : 's'}`;
  const uniqueTotal = parts.names.length + parts.extra;

  return (
    <>
      <CombinedLead>
        <CombinedLeadIcon aria-hidden>
          <LayersOutlined sx={{ fontSize: 13 }} />
        </CombinedLeadIcon>
        {logLabel}
      </CombinedLead>
      {uniqueTotal === 1 && parts.names[0] ? (
        <>
          <CombinedJoiner>of</CombinedJoiner>
          <CombinedTypeChip>
            <CombinedTypeChipIcon aria-hidden>
              <QrCode2Outlined sx={{ fontSize: 12 }} />
            </CombinedTypeChipIcon>
            {parts.names[0]}
          </CombinedTypeChip>
        </>
      ) : null}
      {uniqueTotal > 1 ? (
        <>
          <CombinedJoiner>
            of
            {' '}
            {uniqueTotal.toLocaleString()}
            {' '}
            types
          </CombinedJoiner>
          {parts.names.map((name, index) => (
            <CombinedTypeChip
              key={`${name}_${index}`}
              style={{ animationDelay: `${Math.min(index, 8) * 20}ms` }}
            >
              <CombinedTypeChipIcon aria-hidden>
                <QrCode2Outlined sx={{ fontSize: 12 }} />
              </CombinedTypeChipIcon>
              {name}
            </CombinedTypeChip>
          ))}
          {parts.extra > 0 ? (
            <CombinedMoreChip>
              +
              {parts.extra.toLocaleString()}
              {' '}
              more
            </CombinedMoreChip>
          ) : null}
        </>
      ) : null}
      {uniqueTotal === 0 ? (
        <CombinedJoiner>combined</CombinedJoiner>
      ) : null}
    </>
  );
};
